import React from 'react';
import { Row, Col } from 'react-bootstrap';
import ModalSidebar, {
  ModalSidebarContent,
} from '../../../../components/ModalSidebar';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import { ApprovalRequestTableItem } from '../mappings';
import { ApprovalRequestStatus } from '../../../../__generated__/graphql';
import ApprovalRequestStatusCell from '../ApprovalRequestStatusCell';
import FormattedDateTime from '../../../../../../components/datetime/FormattedDateTime';
import { ReviewActionsCell } from '../ReviewActionsCell';
import { ExpenseDetails } from './ExpenseDetails';
import { EmployeeWithAvatar } from '../../../../components/Employee';

type Props = {
  show: boolean;
  onClose: () => void;
  approvalRequest: ApprovalRequestTableItem | null;
};

const i18Path = 'approvalRequest.detailsSidebar';

export const ApprovalRequestDetailsSidebar: React.FC<Props> = ({
  show,
  onClose,
  approvalRequest,
}) => {
  const t = useNamespacedTranslation(i18Path);

  if (!approvalRequest) {
    return null;
  }

  const renderConditionalContent = () => {
    switch (approvalRequest.relatedRecordApiName) {
      case 'flair__Expense__c':
        return <ExpenseDetails approvalRequest={approvalRequest} />;
      default:
        return null;
    }
  };

  return (
    <ModalSidebar
      show={show}
      onClose={onClose}
      header={t('header')}
      content={
        <ModalSidebarContent
          body={
            <div className="p-4">
              {/* Common Header Section */}
              <div className="border-bottom pb-3 mb-3">
                <Row className="mb-3">
                  <Col sm={6}>
                    <div className="mb-2">
                      <small className="text-muted d-block">
                        {t('recordType')}
                      </small>
                      <span>{approvalRequest.relatedRecordObjectName}</span>
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="mb-2">
                      <small className="text-muted d-block">
                        {t('recordName')}
                      </small>
                      <span>{approvalRequest.relatedRecordName}</span>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col sm={6}>
                    <div className="mb-2">
                      <small className="text-muted d-block">
                        {t('employee')}
                      </small>
                      <EmployeeWithAvatar employee={approvalRequest.employee} />
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="mb-2">
                      <small className="text-muted d-block">
                        {t('requestedAt')}
                      </small>
                      <FormattedDateTime
                        dateTime={approvalRequest.createdDate}
                        format="long"
                      />
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col sm={6}>
                    <div className="mb-2">
                      <small className="text-muted d-block">
                        {t('status')}
                      </small>
                      <ApprovalRequestStatusCell
                        status={approvalRequest.approvalStatus}
                      />
                      {approvalRequest.actionDatetime &&
                        [
                          ApprovalRequestStatus.Approved,
                          ApprovalRequestStatus.Rejected,
                        ].includes(approvalRequest.approvalStatus) && (
                          <FormattedDateTime
                            dateTime={approvalRequest.actionDatetime}
                            format="long"
                          />
                        )}
                    </div>
                  </Col>
                </Row>
                {approvalRequest.approverComment && (
                  <Row className="mb-3">
                    <Col>
                      <div className="mb-2">
                        <small className="text-muted d-block">
                          {t('approverComment')}
                        </small>
                        <span>{approvalRequest.approverComment}</span>
                      </div>
                    </Col>
                  </Row>
                )}
              </div>

              {/* Conditional Content Based on Record Type */}
              {renderConditionalContent()}

              <div className="mt-4">
                {/* Approve/Reject Actions */}
                {approvalRequest.approvalStatus ===
                  ApprovalRequestStatus.Pending && (
                  <Row>
                    <Col>
                      <div className="d-flex gap-2">
                        <ReviewActionsCell id={approvalRequest.id} />
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            </div>
          }
        />
      }
    />
  );
};
